import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Modal, Select, Spin, Input, Popover } from 'antd';
import Button from '../../common/Button';
import IconSvg from '../../common/IconSvg';
import { SelectWrapper, SelectLabel, CustomSelect } from './styled';
import { colors, fontSize } from '../../../constants';
import { themeColors } from '../../../constants/themes';
import ENUMS from '../../../constants/enums';

import AmazonConnect from './AmazonConnect';

const { Option } = Select;

const ConnectAnother = ({ loading, clickHandler }) => {
  return (
    <a
      css={css`
        display: block;
        bottom: 21.4px;
        margin-left: 107px;
        font-family: Roboto;
        font-size: ${fontSize.medium};
        line-height: 1.07;
        letter-spacing: 0;
        text-align: left;
        color: ${colors.blueMed};
        text-decoration: underline;
        ${loading &&
          `
          cursor: default;
          color: ${colors.greyMid};
          &:hover {
            color: ${colors.greyMid};
            text-decoration: underline;
          }
        `}
      `}
      onClick={e => {
        if (!loading) {
          clickHandler(e);
        }
      }}
    >
      {loading && <Spin />}
      <FormattedMessage id="onboarding.screen4.connectAnother" />
    </a>
  );
};

const DestinationResultText = styled.span`
  font-family: Heebo;
  font-size: ${({ theme }) => theme.fontSize.medium};
  line-height: 1.43;
  letter-spacing: normal;
  text-align: left;
  color: ${props => (props.success ? themeColors.green : 'unset')};
`;

const ConnectionResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  height: 7em;
`;

@inject('ebayOAuthConnection')
@inject('azdConnection')
@inject('settingsStore')
@injectIntl
@observer
class ConnectDestination extends Component {
  constructor(props) {
    super();
    this.state = {
      selectedDestinationTypeId: null,
      selectedDestination: null,
      modalVisible: true,
      selectedOldAccount: '',
      okButtonLoading: false,
      connectButtonPressed: false,
      storeName: '',
      paypalAddress: '',
      NonApiToken: '',
      nonApiConnectionError: '',
    };
    this.breadcrumb = {
      name: props.intl.formatMessage({ id: 'settingsModal.connectDestination' }),
    };
  }

  componentDidMount() {
    const {
      ebayOAuthConnection: { reinit },
      azdConnection: { reinit: azReinit },
      updateBreadcrumbs,
    } = this.props;
    updateBreadcrumbs(this.breadcrumb, false);
    reinit();
    azReinit();
  }

  componentDidUpdate() {
    const {
      settingsStore: {
        connectionManagement: { latestAccount, showToast, enums, clearLatestAccount },
      },
    } = this.props;
    if (latestAccount.transferCase === 0) {
      showToast({
        type: enums.TOAST_TYPES.INFO,
        message:
          'This account was connected to our system before. Listings and Orders data was imported successfully',
      });
      clearLatestAccount();
    }
  }

  setModalVisible = val => {
    this.setState({ modalVisible: val });
  };

  setOkButtonLoading = val => {
    this.setState({ okButtonLoading: val });
  };

  setSelectedOldAccount = val => {
    this.setState({ selectedOldAccount: val });
  };

  selectDestinationType = typeId => {
    this.props.fetchDestinations(typeId);
    this.setState({
      selectedDestinationTypeId: typeId.toString(),
      selectedDestination: null,
      connectButtonPressed: false,
    });
  };

  selectDestinationUrl = val => {
    const parsedDest = JSON.parse(val);
    switch (parsedDest.id) {
      case 'EBAY_US': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_GB': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_CA': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_DE': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_IT': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_FR': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'EBAY_ES': {
        this.setState({ selectedDestination: val });
        this.props.ebayOAuthConnection.setDestinationUrl(parsedDest.authorizationUrl);
        break;
      }
      case 'AZD_US': {
        this.setState({ selectedDestination: val });
        break;
      }
      default: {
        break;
      }
    }
  };

  transferAccountData = () => {
    const { selectedOldAccount } = this.state;
    const {
      settingsStore: {
        connectionManagement: { transferListingsOrders },
      },
    } = this.props;
    this.setOkButtonLoading(true);
    transferListingsOrders(selectedOldAccount).then(() => {
      this.setOkButtonLoading(false);
      this.setModalVisible(false);
    });
  };

  handleConnect = () => {
    const {
      ebayOAuthConnection: { connectToEbay },
      azdConnection: { getDeveloperData },
      settingsStore: {
        connectionManagement: { showToast, enums, createNonApiAccount, fetchConnectedAccounts },
      },
      isNonApi,
      selectTab,
    } = this.props;
    const { selectedDestination, storeName, paypalAddress, NonApiToken } = this.state;
    if (isNonApi && (storeName.length < 2 || paypalAddress.length < 2)) {
      showToast({
        type: enums.TOAST_TYPES.WARNING,
        message: 'Please enter a store name',
      });
      return null;
    }
    const parsedDest = JSON.parse(selectedDestination);
    this.setState({ connectButtonPressed: true });
    switch (parsedDest.id) {
      case 'EBAY_US': {
        if (isNonApi) {
          createNonApiAccount(storeName, paypalAddress, NonApiToken).then(resp => {
            if (resp.success) {
              showToast({
                type: enums.TOAST_TYPES.INFO,
                message: 'Request sent successfully!',
              });
              fetchConnectedAccounts().then(() => selectTab(ENUMS.SETTINGS_TABS.CONNECT));
              this.setState({ nonApiConnectionError: '' });
            } else {
              if (resp.message === 'This store is already connected to another account.')
                this.setState({
                  nonApiConnectionError: 'This store is already connected to another account.',
                });
              showToast({
                type: enums.TOAST_TYPES.WARNING,
                message: 'There was an error adding the account',
              });
            }
          });
        } else {
          connectToEbay();
        }
        return null;
      }
      case 'EBAY_GB': {
        return connectToEbay();
      }
      case 'EBAY_DE': {
        return connectToEbay();
      }
      case 'EBAY_IT': {
        return connectToEbay();
      }
      case 'EBAY_FR': {
        return connectToEbay();
      }
      case 'EBAY_ES': {
        return connectToEbay();
      }
      case 'EBAY_CA': {
        return connectToEbay();
      }
      case 'AZD_US': {
        return getDeveloperData();
      }
      default: {
        return null;
      }
    }
  };

  render() {
    const {
      destinationTypeList,
      destinationList,
      ebayOAuthConnection: { connectToEbay, success, firstAccountLoading, ebayWindowOpen, loading },
      azdConnection: { developerData },
      closeForm,
      settingsStore: {
        connectionManagement: { disconnectedAccounts = [''], latestAccount },
      },
      intl: { formatMessage },
      isNonApi,
    } = this.props;
    const {
      selectedDestinationTypeId,
      selectedDestination,
      modalVisible,
      selectedOldAccount,
      okButtonLoading,
      connectButtonPressed,
      storeName,
      paypalAddress,
      NonApiToken,
      nonApiConnectionError,
    } = this.state;

    const parsedDest = JSON.parse(selectedDestination);
    const result = success ? (
      <div>
        <IconSvg type="v-green" size={16} />{' '}
        <DestinationResultText success>
          <FormattedMessage id="onboarding.screen4.accountConnected" />
        </DestinationResultText>
        <Modal
          title={formatMessage({ id: 'connection.importFromAccountTitle' })}
          visible={
            modalVisible && disconnectedAccounts[0] !== '' && latestAccount.transferCase === 1
          }
          width={400}
          okButtonProps={{ disabled: !selectedOldAccount, loading: okButtonLoading }}
          onCancel={() => {
            this.setModalVisible(false);
          }}
          onOk={() => {
            this.transferAccountData();
          }}
        >
          <h2>{formatMessage({ id: 'connection.importFromAccountInfo' })}</h2>
          <Select
            value={selectedOldAccount}
            onChange={this.setSelectedOldAccount}
            css={css`
              width: 200px;
            `}
          >
            {disconnectedAccounts.map(account => (
              <Option value={account.id} key={`sellers-account-${account.id}`}>
                {account.name}
              </Option>
            ))}
            <Option value={JSON.stringify({ id: '', name: '' })} />
          </Select>
        </Modal>
      </div>
    ) : (
      <div>
        <DestinationResultText>
          <FormattedMessage id="onboarding.screen4.failed" />
        </DestinationResultText>
      </div>
    );

    const nonApiDestinationType = [{ id: 'EBAY', name: 'Ebay' }];
    /* eslint-disable */
    const options = isNonApi
      ? nonApiDestinationType.map(type => (
          <Option key={type.id} value={type.id}>
            {type.name}
          </Option>
        ))
      : destinationTypeList.map(type => (
          <Option key={type.id} value={type.id}>
            {type.name}
          </Option>
        ));
    /* eslint-enable */

    return (
      <div style={{ marginTop: '1em' }}>
        <SelectWrapper>
          <SelectLabel>
            <span>1.</span>
            <FormattedMessage
              id="API.selectDestinationType"
              defaultMessage="Select Destination Type"
            />
          </SelectLabel>
          <CustomSelect onChange={this.selectDestinationType}>{options}</CustomSelect>
        </SelectWrapper>

        <SelectWrapper disabled={!selectedDestinationTypeId}>
          <SelectLabel>
            <span>2.</span>
            <FormattedMessage id="API.selectDestination" defaultMessage="Select Destination" />
          </SelectLabel>
          <CustomSelect
            onChange={this.selectDestinationUrl}
            disabled={!selectedDestinationTypeId}
            value={selectedDestination}
          >
            {destinationList.map(dest => (
              <Option key={dest.id} value={JSON.stringify(dest)}>
                {dest.name}
              </Option>
            ))}
          </CustomSelect>
        </SelectWrapper>
        {isNonApi ? (
          <SelectWrapper>
            <SelectLabel>
              <span>3.</span>
              <FormattedMessage id="API.storeName" defaultMessage="Please enter the store name" />
            </SelectLabel>
            <Input
              css={css`
                width: 30%;
              `}
              type="text"
              placeholder="Store Name"
              value={storeName}
              onChange={e => this.setState({ storeName: e.target.value })}
            />
          </SelectWrapper>
        ) : null}
        {isNonApi ? (
          <SelectWrapper>
            <SelectLabel>
              <span>4.</span>
              <FormattedMessage
                id="API.paypalAddress"
                defaultMessage="Please enter the PayPal Account"
              />
            </SelectLabel>
            <Input
              css={css`
                width: 30%;
              `}
              type="text"
              placeholder="PayPal Account"
              value={paypalAddress}
              onChange={e => this.setState({ paypalAddress: e.target.value })}
            />
          </SelectWrapper>
        ) : null}
        {isNonApi ? (
          <SelectWrapper>
            <SelectLabel>
              <span>5.</span>
              <FormattedMessage id="API.nonApiToken" defaultMessage="NON-API token" />
              &nbsp;(optional)
              <Popover
                content={
                  <a
                    css={css`
                      cursor: pointer;
                    `}
                    href="https://signin.ebay.com/ws/eBayISAPI.dll?SignIn&runame=F-FILEEXL51P1EHH6L899Q9B969GE134DK-FileUpload"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    If you are not sure where to get the token from, click here.
                  </a>
                }
                title=""
                trigger="hover"
                placement="bottom"
              >
                <IconSvg
                  type="info"
                  css={css`
                    height: 20px;
                    margin-left: 4px;
                    cursor: pointer;
                  `}
                />
              </Popover>
            </SelectLabel>
            <Input
              css={css`
                width: 30%;
              `}
              type="text"
              placeholder="NON-API token"
              value={NonApiToken}
              onChange={e => this.setState({ NonApiToken: e.target.value })}
            />
          </SelectWrapper>
        ) : null}
        {nonApiConnectionError.length > 1 && (
          <div
            css={css`
              color: red;
            `}
          >
            {nonApiConnectionError}
          </div>
        )}
        {parsedDest?.id !== 'AZD_US' ? (
          <div
            style={{
              width: '75%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {firstAccountLoading ? (
              <Button
                disabled={!selectedDestinationTypeId || !selectedDestination}
                clickHandler={this.handleConnect}
                type="normal"
                loading={ebayWindowOpen || loading}
              >
                <FormattedMessage id="API.connect" defaultMessage="Connect" />
              </Button>
            ) : (
              <ConnectionResultsContainer>
                {!loading && result}
                <ConnectAnother clickHandler={connectToEbay} loading={ebayWindowOpen || loading} />
                <Button
                  clickHandler={() => {
                    closeForm();
                  }}
                  type="normal"
                >
                  <FormattedMessage id="common.back" defaultMessage="Back" />
                </Button>
              </ConnectionResultsContainer>
            )}
          </div>
        ) : (
          <AmazonConnect
            connectButtonPressed={connectButtonPressed}
            handleConnect={this.handleConnect}
            developerData={developerData}
            closeForm={closeForm}
          />
        )}
      </div>
    );
  }
}

ConnectDestination.propTypes = {
  intl: PropTypes.object,
  ebayOAuthConnection: PropTypes.object,
  azdConnection: PropTypes.object,
  settingsStore: PropTypes.object,
  destinationTypeList: PropTypes.array,
  destinationList: PropTypes.array,
  updateBreadcrumbs: PropTypes.func,
  fetchDestinations: PropTypes.func,
  closeForm: PropTypes.func,
  isNonApi: PropTypes.bool,
};

export default ConnectDestination;
